/**
 * DELIV-17370 — non-working (blackout) hours model shared by the Test Controller
 * scoring window editor and the Scoring Leader System Settings page.
 *
 * Mirrors the relational tables: a named group carries the IANA timezone, and each
 * rule row carries weekday flags plus date/time strings interpreted in that zone.
 */

import {
  AssignedItemComponentType,
  ASSIGNED_ITEM_MARKING_TASK_OPTIONS,
  getAssignedItemComponentTypeCaption,
} from '../../ui-scorer/view-scorer-dashboard/models/assigned-item';

export {
  AssignedItemComponentType,
  ASSIGNED_ITEM_MARKING_TASK_OPTIONS,
};

/** Weekday flag columns on `mw_disallowed_time_ranges`, Monday first. */
export const BLACKOUT_DOW_PROPS: { prop: BlackoutDowProp, caption: string, short: string }[] = [
  { prop: 'dow_1_mon', caption: 'Monday', short: 'Mon' },
  { prop: 'dow_2_tue', caption: 'Tuesday', short: 'Tue' },
  { prop: 'dow_3_wed', caption: 'Wednesday', short: 'Wed' },
  { prop: 'dow_4_thu', caption: 'Thursday', short: 'Thu' },
  { prop: 'dow_5_fri', caption: 'Friday', short: 'Fri' },
  { prop: 'dow_6_sat', caption: 'Saturday', short: 'Sat' },
  { prop: 'dow_7_sun', caption: 'Sunday', short: 'Sun' },
];

export type BlackoutDowProp =
  | 'dow_1_mon'
  | 'dow_2_tue'
  | 'dow_3_wed'
  | 'dow_4_thu'
  | 'dow_5_fri'
  | 'dow_6_sat'
  | 'dow_7_sun';

export const BLACKOUT_WEEKDAY_PROPS: BlackoutDowProp[] = [
  'dow_1_mon', 'dow_2_tue', 'dow_3_wed', 'dow_4_thu', 'dow_5_fri',
];
export const BLACKOUT_WEEKEND_PROPS: BlackoutDowProp[] = ['dow_6_sat', 'dow_7_sun'];

export type BlackoutDowPreset = 'weekdays' | 'weekend' | 'all' | 'none';

/** Apply Mon–Fri / Sat–Sun / Every day / Clear to a rule's weekday flags. */
export const applyDowPreset = (
  range: IDisallowedTimeRange,
  preset: BlackoutDowPreset,
): IDisallowedTimeRange => {
  const on: BlackoutDowProp[] =
    preset === 'weekdays' ? BLACKOUT_WEEKDAY_PROPS
    : preset === 'weekend' ? BLACKOUT_WEEKEND_PROPS
    : preset === 'all' ? BLACKOUT_DOW_PROPS.map((d) => d.prop)
    : [];
  const next = { ...range };
  BLACKOUT_DOW_PROPS.forEach((d) => {
    next[d.prop] = on.includes(d.prop) ? 1 : 0;
  });
  return next;
};

/** One `mw_disallowed_time_ranges` row. */
export interface IDisallowedTimeRange {
  id?: number;
  /** Optional friendly label shown in the rule list */
  name?: string | null;
  dow_1_mon?: number;
  dow_2_tue?: number;
  dow_3_wed?: number;
  dow_4_thu?: number;
  dow_5_fri?: number;
  dow_6_sat?: number;
  dow_7_sun?: number;
  /** YYYY-MM-DD inclusive, or null for a recurring weekday rule */
  date_start?: string | null;
  /** YYYY-MM-DD inclusive; null with a `date_start` means a single day */
  date_end?: string | null;
  /** HH:mm in the group timezone */
  time_start?: string | null;
  /** HH:mm; earlier than `time_start` means the rule runs overnight */
  time_end?: string | null;
  /** 1 = clears blackout for the matched day instead of blocking it */
  is_allow_all_day?: number;
  /** Task types this rule blocks; empty is read as Scoring only */
  marking_tasks?: string[] | string | null;
  /** Optional scope — empty/null on a column means no filter on that dimension */
  scope_batch_alloc_policy_ids?: number[] | string | null;
  scope_marking_window_item_ids?: number[] | string | null;
  scope_scorer_uids?: number[] | string | null;
}

/** One `mw_disallowed_time_range_groups` row with its active rules. */
export interface IDisallowedTimeRangeGroup {
  id?: number;
  name?: string;
  /** School year YYYY-YYYY */
  academic_year?: string;
  timezone?: string;
  test_ctrl_group_id?: number;
  created_on?: string;
  ranges?: IDisallowedTimeRange[];
  /** Scoring windows currently using this group */
  attached_window_ids?: number[];
  /** Names for `attached_window_ids`, used by the scope warning */
  attached_windows?: IMarkingWindowLabel[];
}

/** Minimal scoring window identity used in the scope warning and group picker. */
export interface IMarkingWindowLabel {
  id: number;
  name?: string | null;
  lang?: string | null;
  is_locked?: number;
}

/** `#81 PJ Language` — falls back to the bare id when the name is unavailable. */
export const markingWindowLabel = (window: IMarkingWindowLabel): string => {
  const name = window?.name != null ? String(window.name).trim() : '';
  return name ? `#${window.id} ${name}` : `#${window.id}`;
};

/**
 * Scoring windows a group edit reaches besides the one being configured — the
 * rule rows are shared, so saving here saves for all of them.
 */
export const otherAttachedWindows = (
  group: IDisallowedTimeRangeGroup | null | undefined,
  markingWindowId: number,
): IMarkingWindowLabel[] => {
  const labelled = group?.attached_windows;
  const windows: IMarkingWindowLabel[] = labelled && labelled.length
    ? labelled
    : (group?.attached_window_ids || []).map((id) => ({ id: Number(id) }));
  return windows.filter((w) => Number(w.id) !== Number(markingWindowId));
};

/** Administration test window row for attaching a group to scoring windows. */
export interface IBlackoutTestWindowOption {
  id: number;
  title: string;
  academic_year?: string;
  date_start?: string;
  date_end?: string;
  test_ctrl_group_id?: number;
  marking_window_ids: number[];
}

/** Parse `group_concat(marking_window_id)` from administration-windows. */
export const parseMarkingWindowIds = (
  raw: string | number | null | undefined,
): number[] => {
  if (raw == null || raw === '') return [];
  return String(raw)
    .split(',')
    .map((part) => Number(part.trim()))
    .filter((id) => Number.isFinite(id) && id > 0);
};

export const markingWindowIdSelected = (
  selectedIds: number[],
  id: number,
): boolean => selectedIds.some((sel) => Number(sel) === Number(id));

/** True when every scoring window on the administration window is selected. */
export const isBlackoutTestWindowAttached = (
  tw: IBlackoutTestWindowOption,
  selectedMarkingWindowIds: number[],
): boolean => {
  const ids = tw.marking_window_ids || [];
  if (!ids.length) return false;
  return ids.every((id) => markingWindowIdSelected(selectedMarkingWindowIds, id));
};

/**
 * `trackBy` for the attach list. Its rows are re-mapped to fresh objects on every change-detection
 * pass; untracked, each pass replaces the row under the pointer between mousedown and mouseup and
 * the browser never fires the click.
 */
export const trackBlackoutTestWindowById = (
  _index: number,
  tw: IBlackoutTestWindowOption,
): number => tw.id;

/** Attach or detach all scoring windows for one administration window row. */
export const toggleBlackoutTestWindowAttach = (
  selectedMarkingWindowIds: number[],
  tw: IBlackoutTestWindowOption,
  checked: boolean,
): number[] => {
  const ids = tw.marking_window_ids || [];
  if (!ids.length) return selectedMarkingWindowIds;
  if (checked) {
    return [...new Set([...selectedMarkingWindowIds, ...ids])];
  }
  return selectedMarkingWindowIds.filter(
    (id) => !ids.some((mwId) => Number(mwId) === Number(id)),
  );
};

/** Type-to-filter for the administration window attach list. */
export const filterBlackoutTestWindows = (
  windows: IBlackoutTestWindowOption[],
  searchText: string,
): IBlackoutTestWindowOption[] => {
  const needle = (searchText || '').trim().toLowerCase();
  if (!needle) return windows;
  return windows.filter((tw) => {
    const haystack = `#${tw.id} ${tw.title || ''} ${tw.academic_year || ''} ${(tw.marking_window_ids || []).join(' ')}`.toLowerCase();
    return haystack.indexOf(needle) !== -1;
  });
};

/** Prefer administration windows that already have scoring windows linked. */
export const sortBlackoutTestWindowsForAttach = (
  windows: IBlackoutTestWindowOption[],
): IBlackoutTestWindowOption[] => {
  return [...windows].sort((a, b) => {
    const aHas = (a.marking_window_ids || []).length > 0;
    const bHas = (b.marking_window_ids || []).length > 0;
    if (aHas !== bHas) return aHas ? -1 : 1;
    return b.id - a.id;
  });
};

/** Best-effort label for a test window title JSON/string. */
export const parseTestWindowTitle = (
  title: unknown,
  lang: string = 'en',
): string => {
  if (title == null || title === '') return '';
  if (typeof title === 'object') {
    const obj = title as Record<string, string>;
    return obj[lang] || obj.en || Object.values(obj)[0] || '';
  }
  const raw = String(title);
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') {
      return parsed[lang] || parsed.en || raw;
    }
  } catch {
    // plain string title
  }
  return raw;
};

/** Input for the schedule preview — one or more groups overlaid. */
export interface IBlackoutPreviewGroup {
  key?: string;
  id?: number;
  name?: string;
  timezone?: string;
  ranges?: IDisallowedTimeRange[];
  /** Layer colour when multiple groups are shown together */
  color?: string;
}

/** Result of the API's live schedule preview for a window. */
export interface IDisallowedRangePreview {
  allowed: boolean;
  reason: string;
  localTime?: string;
  rangeId?: number;
  groupId?: number;
  groupName?: string;
}

/** Attached group + rule row blocking scoring at summary load time. */
export interface IBlockingGroupRule {
  group_id: number;
  group_name: string;
  range_id: number;
  rule_name: string;
  rule_schedule?: string;
}

/** Basic `marking_windows` row returned with the schedule payload. */
export interface IMarkingWindowSummary {
  id: number;
  name?: string;
  lang?: string;
  start_on?: string;
  end_on?: string;
  is_active?: number;
  is_archived?: number;
  is_locked?: number;
  is_paused?: number;
  is_scoring_disabled?: number;
  is_hidden_for_scorers?: number;
  /** 1 hides when non-working hours end from scorers; null until the column is deployed */
  is_nwh_resume_time_hidden?: number | null;
  test_window_id?: number;
  academic_year?: string;
}

/** `GET public/scor-lead/marking-window-schedule?summary=1&marking_window_ids[]=…` */
export interface IMarkingWindowScheduleSummary {
  marking_window_id: number;
  is_schema_ready: boolean;
  is_schedule_on: boolean;
  active_group_count?: number;
  active_group_names?: string[];
  active_group_name?: string | null;
  active_group_timezone?: string | null;
  rule_count?: number;
  /** Named rule rows across every attached group (Scoring Leader dashboard). */
  active_rule_names?: string[];
  /** Groups whose rules are blocking at summary load (SL availability banner). */
  blocking_group_rules?: IBlockingGroupRule[];
  preview?: IDisallowedRangePreview;
  summary_label: string;
}

/**
 * Ids per summary request. The API's query parser reads `marking_window_ids[n]` as a list only up
 * to 21 entries; past that, older API builds receive no ids at all (vea-api#4931).
 */
export const SCHEDULE_SUMMARY_IDS_PER_REQUEST = 20;

/** Window ids that were asked for but have no summary yet, in request order, without repeats. */
export const missingScheduleSummaryIds = (
  requested: number[],
  received: Iterable<number>,
): number[] => {
  const have = new Set<number>();
  for (const id of received) have.add(Number(id));
  const seen = new Set<number>();
  const missing: number[] = [];
  for (const id of requested) {
    const n = Number(id);
    if (!have.has(n) && !seen.has(n)) {
      seen.add(n);
      missing.push(n);
    }
  }
  return missing;
};

export const chunkScheduleSummaryIds = (
  ids: number[],
  size: number = SCHEDULE_SUMMARY_IDS_PER_REQUEST,
): number[][] => {
  const step = Math.max(1, Math.floor(size));
  const chunks: number[][] = [];
  for (let i = 0; i < ids.length; i += step) {
    chunks.push(ids.slice(i, i + step));
  }
  return chunks;
};

/** `GET public/test-ctrl/marking-window-schedule/:marking_window_id` */
export interface IMarkingWindowSchedule {
  marking_window_id: number;
  marking_window?: IMarkingWindowSummary | null;
  /** Academic year of the scoring window's test window */
  window_academic_year?: string | null;
  /** False until the `mw_*` tables are deployed; enforcement fails open meanwhile */
  is_schema_ready: boolean;
  is_schedule_on: boolean;
  /** Every group enforced on this window — a window may carry several. */
  activeGroups: IDisallowedTimeRangeGroup[];
  active_group_ids?: number[];
  /** First attached group; kept for callers that render a single label. */
  activeGroup: IDisallowedTimeRangeGroup | null;
  groups: IDisallowedTimeRangeGroup[];
  history: IDisallowedTimeRangeGroupLink[];
  preview: IDisallowedRangePreview;
  markingTaskOptions: string[];
  scopePickerOptions?: IRuleScopePickerOptions;
}

export interface IRuleScopePickerOptions {
  batch_alloc_policies: { id: number; slug?: string | null }[];
  marking_window_items: {
    id: number;
    caption?: string | null;
    slug?: string | null;
    batch_alloc_policy_id?: number | null;
  }[];
  scorers: { uid: number; first_name?: string | null; last_name?: string | null }[];
}

export interface IDisallowedTimeRangeGroupLink {
  id: number;
  mw_disallowed_time_range_group_id: number;
  group_name?: string;
  created_on?: string;
  created_by_uid?: number;
  is_revoked?: number;
  revoked_on?: string;
  revoked_by_uid?: number;
}

export const DEFAULT_BLACKOUT_TIMEZONE = 'America/Toronto';

/** Sentinel group ids in the window picker: no schedule, and "create a new group". */
export const BLACKOUT_GROUP_OFF = 0;
export const BLACKOUT_GROUP_NEW = -1;

/** Add or remove one group id from the window's selected set. */
export const toggleGroupSelection = (
  selectedGroupIds: number[],
  groupId: number,
): number[] => {
  const id = Number(groupId);
  if (!Number.isFinite(id) || id <= 0) return selectedGroupIds;
  return isGroupSelected(selectedGroupIds, id)
    ? selectedGroupIds.filter((selected) => Number(selected) !== id)
    : [...selectedGroupIds, id];
};

export const isGroupSelected = (
  selectedGroupIds: number[],
  groupId: number,
): boolean => (selectedGroupIds || []).some((selected) => Number(selected) === Number(groupId));

export interface IEditorStateAfterSave {
  selectedGroupIds: number[];
  editingGroupId: number;
  selectedRuleIndex: number | null;
}

/**
 * Editor state to restore once saved group rules have been reloaded. The reload resets the
 * checked set to what is attached on the server; a group checked but not yet applied, the
 * group being edited and the rule being edited all survive a Save.
 */
export const editorStateAfterSave = (input: {
  checkedBefore: number[];
  savedGroupId: number;
  ruleIndexBefore: number | null;
  ruleCountAfter: number;
}): IEditorStateAfterSave => {
  const savedGroupId = Number(input.savedGroupId) || 0;
  const checked = (input.checkedBefore || []).map(Number).filter((id) => Number.isFinite(id) && id > 0);
  const count = Math.max(0, Number(input.ruleCountAfter) || 0);
  let selectedRuleIndex: number | null = null;
  if (count > 0) {
    const before = input.ruleIndexBefore == null ? 0 : Number(input.ruleIndexBefore);
    selectedRuleIndex = Math.min(Math.max(0, before), count - 1);
  }
  return {
    selectedGroupIds: [...new Set(checked)],
    editingGroupId: savedGroupId,
    selectedRuleIndex,
  };
};

/** True when the selected set differs from what is attached on the server. */
export const isGroupSelectionDirty = (
  selectedGroupIds: number[],
  attachedGroupIds: number[],
): boolean => {
  const a = [...(selectedGroupIds || [])].map(Number).sort((x, y) => x - y);
  const b = [...(attachedGroupIds || [])].map(Number).sort((x, y) => x - y);
  return a.length !== b.length || a.some((id, i) => id !== b[i]);
};

/**
 * Windows other than this one reached by editing any of the selected groups.
 * Deduplicated, because two selected groups often share a window.
 */
export const otherWindowsForGroups = (
  groups: IDisallowedTimeRangeGroup[],
  markingWindowId: number,
): IMarkingWindowLabel[] => {
  const byId = new Map<number, IMarkingWindowLabel>();
  (groups || []).forEach((group) => {
    otherAttachedWindows(group, markingWindowId).forEach((window) => {
      if (!byId.has(Number(window.id))) byId.set(Number(window.id), window);
    });
  });
  return [...byId.values()];
};

/** Which groups the picker lists. */
export type BlackoutGroupScopeFilter = 'all' | 'this-window' | 'shared' | 'unused';

export const BLACKOUT_GROUP_SCOPE_FILTERS: { value: BlackoutGroupScopeFilter, label: string }[] = [
  { value: 'all', label: 'All groups' },
  { value: 'this-window', label: 'Used by this window' },
  { value: 'shared', label: 'Used by other windows' },
  { value: 'unused', label: 'Not attached anywhere' },
];

/** Timezones offered in the group editor; free text is still accepted. */
export const BLACKOUT_TIMEZONE_OPTIONS: string[] = [
  'America/Toronto',
  'America/Winnipeg',
  'America/Edmonton',
  'America/Vancouver',
  'America/Halifax',
  'America/St_Johns',
  'UTC',
];

/** The `marking_tasks` JSON column may arrive as an array or a JSON string. */
export const parseMarkingTasks = (raw: IDisallowedTimeRange['marking_tasks']): string[] => {
  let value: any = raw;
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value);
    } catch {
      return [AssignedItemComponentType.SCORING];
    }
  }
  if (!Array.isArray(value) || value.length === 0) return [AssignedItemComponentType.SCORING];
  return value.map((v) => String(v));
};

/** Normalize a scope JSON column to a sorted unique positive-int list. */
export const parseScopeIds = (raw: unknown): number[] => {
  let value: unknown = raw;
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(value)) return [];
  const ids = value
    .map((v) => Number(v))
    .filter((id) => Number.isFinite(id) && id > 0);
  return [...new Set(ids)].sort((a, b) => a - b);
};

export const hasRuleScope = (range: IDisallowedTimeRange): boolean =>
  parseScopeIds(range?.scope_batch_alloc_policy_ids).length > 0
  || parseScopeIds(range?.scope_marking_window_item_ids).length > 0
  || parseScopeIds(range?.scope_scorer_uids).length > 0;

export const describeScopeSummary = (
  range: IDisallowedTimeRange,
  options?: IRuleScopePickerOptions | null,
): string => {
  if (!range || !hasRuleScope(range)) return '';
  const parts: string[] = [];
  const policies = parseScopeIds(range.scope_batch_alloc_policy_ids);
  if (policies.length) {
    const labels = policies.map((id) => {
      const row = options?.batch_alloc_policies?.find((p) => Number(p.id) === id);
      return row?.slug ? `#${id} ${row.slug}` : `#${id}`;
    });
    parts.push(`policies: ${labels.join(', ')}`);
  }
  const items = parseScopeIds(range.scope_marking_window_item_ids);
  if (items.length) {
    const labels = items.map((id) => {
      const row = options?.marking_window_items?.find((p) => Number(p.id) === id);
      const caption = row?.caption || row?.slug;
      return caption ? `#${id} ${caption}` : `#${id}`;
    });
    parts.push(`items: ${labels.join(', ')}`);
  }
  const scorers = parseScopeIds(range.scope_scorer_uids);
  if (scorers.length) {
    const labels = scorers.map((uid) => {
      const row = options?.scorers?.find((p) => Number(p.uid) === uid);
      const name = [row?.first_name, row?.last_name].filter(Boolean).join(' ').trim();
      return name ? `${name} (#${uid})` : `#${uid}`;
    });
    parts.push(`scorers: ${labels.join(', ')}`);
  }
  return parts.join(' · ');
};

/** A rule with no clock span, or with equal start/end, covers the whole matched day. */
export const isAllDayRange = (range: IDisallowedTimeRange): boolean => {
  if (!range?.time_start || !range?.time_end) return true;
  return range.time_start === range.time_end;
};

/** All day as the rule form means it: no clock span at all. Equal times are a typo, not all day. */
export const isExplicitAllDayRange = (range: IDisallowedTimeRange): boolean =>
  !range?.time_start && !range?.time_end;

/** True when the rule crosses midnight, so the morning belongs to the prior day's shift. */
export const isOvernightRange = (range: IDisallowedTimeRange): boolean => {
  if (isAllDayRange(range)) return false;
  return range.time_end < range.time_start;
};

export const isDateScopedRange = (range: IDisallowedTimeRange): boolean => !!range?.date_start;

const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;
const TIME_12H_PATTERN = /^(\d{1,2}):([0-5]\d)\s*(AM|PM)$/i;

export const isValidDateStr = (value?: string | null): boolean =>
  !value || DATE_PATTERN.test(value);

export const isValidTimeStr = (value?: string | null): boolean =>
  !value || TIME_PATTERN.test(value);

/** Hour values 00–23 for the rule editor dropdowns. */
export const BLACKOUT_HOUR_OPTIONS: string[] = Array.from(
  { length: 24 },
  (_, hour) => String(hour).padStart(2, '0'),
);

/** Minute values in 15-minute steps for the rule editor dropdowns. */
export const BLACKOUT_MINUTE_OPTIONS: string[] = ['00', '15', '30', '45'];

export interface BlackoutTimeParts {
  hour: string;
  minute: string;
}

/** Snap a minute value to the nearest 15-minute step (0, 15, 30, 45). */
export const snapMinuteToQuarter = (minute: number): string => {
  const snapped = Math.min(45, Math.round(minute / 15) * 15);
  return String(snapped).padStart(2, '0');
};

/**
 * Normalize a clock value from `<input type="time">` (HH:mm) or occasional 12h
 * strings into zero-padded 24-hour HH:mm for storage and overnight checks.
 */
export const normalizeTimeHm = (value?: string | null): string | null => {
  if (value == null) return null;
  const trimmed = String(value).trim();
  if (!trimmed) return null;

  const match24 = trimmed.match(/^(\d{1,2}):([0-5]\d)$/);
  if (match24) {
    const hours = Number(match24[1]);
    if (hours > 23) return trimmed;
    return `${String(hours).padStart(2, '0')}:${match24[2]}`;
  }

  const match12 = trimmed.match(TIME_12H_PATTERN);
  if (match12) {
    let hours = Number(match12[1]) % 12;
    if (match12[3].toUpperCase() === 'PM') hours += 12;
    return `${String(hours).padStart(2, '0')}:${match12[2]}`;
  }

  return trimmed;
};

/** Split HH:mm into dropdown parts, snapping minutes to 15-minute steps. */
export const parseTimeParts = (value?: string | null): BlackoutTimeParts => {
  const normalized = normalizeTimeHm(value);
  if (!normalized) return { hour: '00', minute: '00' };
  const [hourRaw, minuteRaw] = normalized.split(':');
  return {
    hour: String(Number(hourRaw)).padStart(2, '0'),
    minute: snapMinuteToQuarter(Number(minuteRaw)),
  };
};

/** Build HH:mm from hour/minute dropdown values. */
export const buildTimeHm = (hour: string, minute: string): string => {
  const h = Number(hour);
  const safeHour = Number.isFinite(h) ? Math.min(23, Math.max(0, h)) : 0;
  return `${String(safeHour).padStart(2, '0')}:${snapMinuteToQuarter(Number(minute))}`;
};

/** Human-readable long date for the rule editor (e.g. "Friday, September 4, 2026"). */
export const formatDateWritten = (value?: string | null): string | null => {
  if (!value || !DATE_PATTERN.test(value)) return null;
  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-CA', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

export interface BlackoutDateParts {
  year: string;
  month: string;
  day: string;
}

/** Month labels for the date dropdown (value is MM). */
export const BLACKOUT_MONTH_OPTIONS: { value: string; label: string }[] = [
  { value: '01', label: 'Jan' },
  { value: '02', label: 'Feb' },
  { value: '03', label: 'Mar' },
  { value: '04', label: 'Apr' },
  { value: '05', label: 'May' },
  { value: '06', label: 'Jun' },
  { value: '07', label: 'Jul' },
  { value: '08', label: 'Aug' },
  { value: '09', label: 'Sep' },
  { value: '10', label: 'Oct' },
  { value: '11', label: 'Nov' },
  { value: '12', label: 'Dec' },
];

const daysInMonth = (year: number, month: number): number =>
  new Date(year, month, 0).getDate();

/** Year values for the date dropdown (current year −2 through +8). */
export const getBlackoutYearOptions = (): string[] => {
  const current = new Date().getFullYear();
  const start = current - 2;
  const end = current + 8;
  return Array.from({ length: end - start + 1 }, (_, i) => String(start + i));
};

/** Day values (DD) for the selected month; defaults to 1–31 when month/year unset. */
export const getBlackoutDayOptions = (year: string, month: string): string[] => {
  if (!year || !month) {
    return Array.from({ length: 31 }, (_, i) => String(i + 1).padStart(2, '0'));
  }
  const count = daysInMonth(Number(year), Number(month));
  return Array.from({ length: count }, (_, i) => String(i + 1).padStart(2, '0'));
};

/** Split YYYY-MM-DD into dropdown parts. */
export const parseDateParts = (value?: string | null): BlackoutDateParts => {
  if (!value || !DATE_PATTERN.test(value)) {
    return { year: '', month: '', day: '' };
  }
  const [year, month, day] = value.split('-');
  return { year, month, day };
};

/** Assemble YYYY-MM-DD from dropdown parts; null when incomplete or invalid. */
export const buildDateYmd = (year: string, month: string, day: string): string | null => {
  if (!year || !month || !day) return null;
  const y = Number(year);
  const m = Number(month);
  const d = Number(day);
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return null;
  if (m < 1 || m > 12 || d < 1) return null;
  const maxDay = daysInMonth(y, m);
  if (d > maxDay) return null;
  return `${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
};

/** Snap stored clock values to 15-minute steps when loading a rule for editing. */
export const normalizeRangeTimesForEdit = (range: IDisallowedTimeRange): IDisallowedTimeRange => {
  if (!range || Number(range.is_allow_all_day)) return range;
  const next = { ...range };
  if (next.time_start) {
    const parts = parseTimeParts(next.time_start);
    next.time_start = buildTimeHm(parts.hour, parts.minute);
  }
  if (next.time_end) {
    const parts = parseTimeParts(next.time_end);
    next.time_end = buildTimeHm(parts.hour, parts.minute);
  }
  // The API reads equal times as all day; load them as the explicit form so they stay valid.
  if (next.time_start && next.time_start === next.time_end) {
    next.time_start = null;
    next.time_end = null;
  }
  return next;
};

/** A new recurring weekday rule pre-filled with the common overnight blackout. */
export const newRecurringRange = (): IDisallowedTimeRange => ({
  dow_1_mon: 1,
  dow_2_tue: 1,
  dow_3_wed: 1,
  dow_4_thu: 1,
  dow_5_fri: 1,
  dow_6_sat: 0,
  dow_7_sun: 0,
  date_start: null,
  date_end: null,
  time_start: '18:00',
  time_end: '08:00',
  is_allow_all_day: 0,
  marking_tasks: [AssignedItemComponentType.SCORING],
});

/** A new single-date rule; `isAllowAllDay` makes it an exception instead of a blackout. */
export const newDateRange = (isAllowAllDay = false): IDisallowedTimeRange => ({
  dow_1_mon: 0,
  dow_2_tue: 0,
  dow_3_wed: 0,
  dow_4_thu: 0,
  dow_5_fri: 0,
  dow_6_sat: 0,
  dow_7_sun: 0,
  date_start: null,
  date_end: null,
  time_start: null,
  time_end: null,
  is_allow_all_day: isAllowAllDay ? 1 : 0,
  marking_tasks: [AssignedItemComponentType.SCORING],
});

/** First validation message for a rule, or null when the rule is well-formed. */
export const validateRange = (range: IDisallowedTimeRange): string | null => {
  if (!range) return 'Rule is empty.';
  if (range.name != null && String(range.name).trim().length > 255) {
    return 'Rule name cannot exceed 255 characters.';
  }
  const hasDow = BLACKOUT_DOW_PROPS.some((d) => Number(range[d.prop]) === 1);
  if (!hasDow && !range.date_start) {
    return 'Choose at least one weekday, or set a specific date.';
  }
  if (!isValidDateStr(range.date_start) || !isValidDateStr(range.date_end)) {
    return 'Dates must use YYYY-MM-DD.';
  }
  if (range.date_end && !range.date_start) {
    return 'An end date needs a start date.';
  }
  if (range.date_start && range.date_end && range.date_end < range.date_start) {
    return 'End date is before the start date.';
  }
  if (!Number(range.is_allow_all_day)) {
    if (!isValidTimeStr(range.time_start) || !isValidTimeStr(range.time_end)) {
      return 'Times must use HH:mm (24-hour).';
    }
    const hasOneTime = !!range.time_start !== !!range.time_end;
    if (hasOneTime) return 'Set both a start and an end time, or neither for an all-day rule.';
    if (range.time_start && range.time_start === range.time_end) {
      return 'From and Until are the same time. Tick All day for a 24-hour block, or change one of the times.';
    }
  }
  if (parseMarkingTasks(range.marking_tasks).length === 0) {
    return 'Select at least one task type.';
  }
  return null;
};

/** First validation message across a rule set, or null when every rule is well-formed. */
export const validateRanges = (ranges: IDisallowedTimeRange[]): string | null => {
  for (let i = 0; i < (ranges || []).length; i++) {
    const message = validateRange(ranges[i]);
    if (message) return `Rule ${i + 1}: ${message}`;
  }
  return null;
};

/** Collapsed-rule title: custom name when set, otherwise a generic label. */
export const ruleDisplayTitle = (range: IDisallowedTimeRange): string => {
  const name = range?.name != null ? String(range.name).trim() : '';
  return name || 'Unnamed rule';
};

/**
 * Titles for the rule list. A title that appears more than once (duplicates, several unnamed
 * rules) is numbered in list order — "Unnamed rule #1", "Unnamed rule #2" — so rows can be told apart.
 */
export const ruleChipTitles = (ranges: IDisallowedTimeRange[]): string[] => {
  const titles = (ranges || []).map((range) => ruleDisplayTitle(range));
  const totals = new Map<string, number>();
  titles.forEach((title) => totals.set(title, (totals.get(title) || 0) + 1));
  const seen = new Map<string, number>();
  return titles.map((title) => {
    if ((totals.get(title) || 0) < 2) return title;
    const n = (seen.get(title) || 0) + 1;
    seen.set(title, n);
    return `${title} #${n}`;
  });
};

/** Plain-language summary of a rule schedule (times, days, tasks). */
export const describeRangeSchedule = (
  range: IDisallowedTimeRange,
  scopeOptions?: IRuleScopePickerOptions | null,
): string => {
  if (!range) return '';
  const days = BLACKOUT_DOW_PROPS.filter((d) => Number(range[d.prop]) === 1).map((d) => d.short);
  const parts: string[] = [];
  if (range.date_start && range.date_end) parts.push(`${range.date_start} to ${range.date_end}`);
  else if (range.date_start) parts.push(range.date_start);
  if (days.length === 7) parts.push('every day');
  else if (days.length) parts.push(days.join(', '));
  if (Number(range.is_allow_all_day)) {
    parts.push('available all day');
  } else if (isAllDayRange(range)) {
    parts.push('all day');
  } else {
    parts.push(`${range.time_start}–${range.time_end}${isOvernightRange(range) ? ' (overnight)' : ''}`);
  }
  const tasks = parseMarkingTasks(range.marking_tasks)
    .map((t) => getAssignedItemComponentTypeCaption(t as AssignedItemComponentType) || t);
  parts.push(tasks.join(' / '));
  const scope = describeScopeSummary(range, scopeOptions);
  if (scope) parts.push(`scope: ${scope}`);
  return parts.join(' · ');
};

/** Plain-language summary of a rule, used in read-only lists. */
export const describeRange = (
  range: IDisallowedTimeRange,
  scopeOptions?: IRuleScopePickerOptions | null,
): string => {
  const schedule = describeRangeSchedule(range, scopeOptions);
  const name = range?.name != null ? String(range.name).trim() : '';
  if (name) return `${name} · ${schedule}`;
  return schedule;
};

/** Strip client-only fields before sending a rule set to the API. */
export const serializeRanges = (ranges: IDisallowedTimeRange[]): IDisallowedTimeRange[] =>
  (ranges || []).map((r) => ({
    name: r.name != null && String(r.name).trim() ? String(r.name).trim() : null,
    dow_1_mon: Number(r.dow_1_mon) ? 1 : 0,
    dow_2_tue: Number(r.dow_2_tue) ? 1 : 0,
    dow_3_wed: Number(r.dow_3_wed) ? 1 : 0,
    dow_4_thu: Number(r.dow_4_thu) ? 1 : 0,
    dow_5_fri: Number(r.dow_5_fri) ? 1 : 0,
    dow_6_sat: Number(r.dow_6_sat) ? 1 : 0,
    dow_7_sun: Number(r.dow_7_sun) ? 1 : 0,
    date_start: r.date_start || null,
    date_end: r.date_end || null,
    time_start: Number(r.is_allow_all_day) ? null : (r.time_start || null),
    time_end: Number(r.is_allow_all_day) ? null : (r.time_end || null),
    is_allow_all_day: Number(r.is_allow_all_day) ? 1 : 0,
    marking_tasks: parseMarkingTasks(r.marking_tasks),
    scope_batch_alloc_policy_ids: parseScopeIds(r.scope_batch_alloc_policy_ids),
    scope_marking_window_item_ids: parseScopeIds(r.scope_marking_window_item_ids),
    scope_scorer_uids: parseScopeIds(r.scope_scorer_uids),
  }));

/** Deep copy of the rules on a group, normalized for editing. */
export const cloneRangesForEdit = (
  group: IDisallowedTimeRangeGroup | null | undefined,
): IDisallowedTimeRange[] =>
  (group?.ranges || []).map((r) => cloneRangeForEdit(r));

/** Deep copy of one rule row, normalized for editing. */
export const cloneRangeForEdit = (r: IDisallowedTimeRange): IDisallowedTimeRange =>
  normalizeRangeTimesForEdit({
    ...r,
    date_start: r.date_start || null,
    date_end: r.date_end || null,
    marking_tasks: parseMarkingTasks(r.marking_tasks),
    scope_batch_alloc_policy_ids: parseScopeIds(r.scope_batch_alloc_policy_ids),
    scope_marking_window_item_ids: parseScopeIds(r.scope_marking_window_item_ids),
    scope_scorer_uids: parseScopeIds(r.scope_scorer_uids),
  });

/**
 * Whether leaving the editor now would lose work: a new-group draft with a name or rules, or
 * rule / timezone edits on the open group that differ from what was loaded.
 */
export const hasUnsavedBlackoutEdits = (state: {
  isCreatingGroup: boolean;
  newGroupName?: string;
  editRanges: IDisallowedTimeRange[];
  editTimezone?: string;
  savedGroup?: IDisallowedTimeRangeGroup | null;
}): boolean => {
  const ranges = state.editRanges || [];
  if (state.isCreatingGroup) {
    return !!String(state.newGroupName || '').trim() || ranges.length > 0;
  }
  if (!state.savedGroup) return false;
  if ((state.editTimezone || DEFAULT_BLACKOUT_TIMEZONE) !== (state.savedGroup.timezone || DEFAULT_BLACKOUT_TIMEZONE)) {
    return true;
  }
  const saved = cloneRangesForEdit(state.savedGroup);
  return JSON.stringify(serializeRanges(saved)) !== JSON.stringify(serializeRanges(ranges));
};

/** Plain-language text for the codes the groups / schedule services throw. */
const BLACKOUT_API_ERROR_MESSAGES: Record<string, string> = {
  ACADEMIC_YEAR_REQUIRED: 'Enter the academic year for the new group (e.g. 2025-2026).',
  INVALID_ACADEMIC_YEAR_FORMAT: 'Academic year must look like 2025-2026.',
  INVALID_ACADEMIC_YEAR_SPAN: 'Academic year must span two consecutive years (e.g. 2025-2026).',
  GROUP_ACADEMIC_YEAR_MISMATCH: 'This group belongs to a different academic year than this scoring window.',
  GROUP_NAME_REQUIRED: 'Enter a name for the group.',
  GROUP_NAME_TOO_LONG: 'Group name cannot exceed 255 characters.',
  GROUP_NAME_ALREADY_IN_USE: 'A group with this name already exists for this academic year. Choose another name.',
  RULE_NAME_TOO_LONG: 'Rule name cannot exceed 255 characters.',
  INVALID_DATE_FORMAT_EXPECTED_YYYY_MM_DD: 'Dates must use YYYY-MM-DD.',
  INVALID_TIME_FORMAT_EXPECTED_HH_MM: 'Times must use HH:mm (24-hour).',
  DATE_END_BEFORE_DATE_START: 'End date is before the start date.',
  DATE_END_WITHOUT_DATE_START: 'An end date needs a start date.',
  RANGE_NEEDS_A_WEEKDAY_OR_A_DATE: 'Each rule needs at least one weekday or a specific date.',
  INVALID_MARKING_TASK: 'A rule lists a task type that is not recognised.',
  INVALID_SCOPE_POLICY_ID: 'A rule is scoped to a batch allocation policy that no longer exists.',
  INVALID_SCOPE_ITEM_ID: 'A rule is scoped to an item that no longer exists.',
  INVALID_SCOPE_SCORER_UID: 'A rule is scoped to a scorer that no longer exists.',
  INVALID_TIMEZONE: 'Choose a timezone from the list.',
  DISALLOWED_TIME_RANGE_GROUP_NOT_FOUND: 'This group no longer exists. Reload the page.',
  'INVALID-test_ctrl_group_id': 'This group belongs to a different program and cannot be used here.',
  DISALLOWED_TIME_RANGE_SCHEMA_NOT_DEPLOYED: 'Non-working hours are not available on this environment yet.',
  DISALLOWED_TIME_RANGE_SCHEMA_INCOMPLETE: 'Non-working hours are not fully set up on this environment. Contact support.',
  NWH_RESUME_TIME_FLAG_NOT_DEPLOYED: 'This setting is not available on this environment yet.',
  MARKING_WINDOW_NOT_FOUND: 'This scoring window could not be found. Reload the page.',
};

/**
 * Popup text for a failed non-working hours call. Known codes become a sentence; anything
 * else keeps the fallback and the raw code so support can still trace it.
 */
export const describeBlackoutApiError = (err: any, fallback: string): string => {
  const code = String(err?.message || '').trim();
  if (!code) return fallback;
  const known = BLACKOUT_API_ERROR_MESSAGES[code] || BLACKOUT_API_ERROR_MESSAGES[code.split(':')[0]];
  return known || `${fallback} (${code})`;
};

const ACADEMIC_YEAR_PATTERN = /^(\d{4})-(\d{4})$/;

/** Message for an invalid school year (`YYYY-YYYY`, consecutive), or null when valid. */
export const validateAcademicYear = (raw: string | null | undefined): string | null => {
  const value = String(raw || '').trim().replace(/\u2013/g, '-');
  if (!value) return 'Enter the academic year for the new group (e.g. 2025-2026).';
  const match = ACADEMIC_YEAR_PATTERN.exec(value);
  if (!match) return 'Academic year must look like 2025-2026.';
  if (Number(match[2]) !== Number(match[1]) + 1) {
    return 'Academic year must span two consecutive years (e.g. 2025-2026).';
  }
  return null;
};

/**
 * Body for `create` on the groups route. The API requires `academic_year`; it can only derive
 * one from `marking_window_id`, which would also attach the group, so the year is always sent.
 */
export const buildNewGroupPayload = (draft: {
  name: string;
  timezone: string;
  academicYear: string;
  ranges: IDisallowedTimeRange[];
}) => ({
  name: String(draft.name || '').trim(),
  timezone: draft.timezone,
  academic_year: String(draft.academicYear || '').trim().replace(/\u2013/g, '-'),
  ranges: serializeRanges(draft.ranges),
});

/** Compare two rules using the same normalization as API serialization. */
export const rangesEqual = (a: IDisallowedTimeRange, b: IDisallowedTimeRange): boolean =>
  JSON.stringify(serializeRanges([a])) === JSON.stringify(serializeRanges([b]));

/**
 * Merge the scope picker options of every attached window (de-duplicated by id / uid).
 * A schedule with a missing or non-array list is an unreadable window, never an empty one.
 */
export const mergeScopePickerOptions = (schedules: any[]): IRuleScopePickerOptions => {
  const keys = ['batch_alloc_policies', 'marking_window_items', 'scorers'];
  if (schedules.some(s => !s || !s.scopePickerOptions || keys.some(k => !Array.isArray(s.scopePickerOptions[k])))) {
    throw new Error('Incomplete scope options');
  }
  const merge = (key: string, identity: string) => {
    const rows = new Map<number, any>();
    schedules.forEach(s => s.scopePickerOptions[key].forEach((row: any) => rows.set(Number(row[identity]), row)));
    return [...rows.values()];
  };
  return {
    batch_alloc_policies: merge('batch_alloc_policies', 'id'),
    marking_window_items: merge('marking_window_items', 'id'),
    scorers: merge('scorers', 'uid'),
  };
};
